import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { FiBookOpen, FiClipboard, FiGrid, FiLogOut, FiMapPin, FiSliders, FiTruck, FiUsers } from "react-icons/fi";
import { usePmsTheme } from "../Components/PmsThemeContext";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import "./CorporatePurchasingShell.css";

const COPY = {
  en: {
    title: "Corporate Purchasing", navigation: "Corporate purchasing navigation",
    scope: "Shared catalog", workspace: "Choose workspace", logout: "Logout", usersRoles: "Users & Roles",
    pin: "Pin sidebar", unpin: "Unpin sidebar",
    menus: { tenders: "Tenders", catalog: "Corporate catalog", suppliers: "Suppliers", units: "Units of measure" },
  },
  es: {
    title: "Compras corporativas", navigation: "Navegación de compras corporativas",
    scope: "Catálogo compartido", workspace: "Elegir espacio", logout: "Cerrar sesión", usersRoles: "Usuarios y roles",
    pin: "Fijar menú lateral", unpin: "Desfijar menú lateral",
    menus: { tenders: "Licitaciones", catalog: "Catálogo corporativo", suppliers: "Proveedores", units: "Unidades de medida" },
  },
};
const MENUS = [
  { key: "tenders", Icon: FiClipboard },
  { key: "catalog", Icon: FiBookOpen },
  { key: "suppliers", Icon: FiTruck },
  { key: "units", Icon: FiSliders },
];

export default function CorporatePurchasingPanel({ user }) {
  const { themeId, languageId } = usePmsTheme();
  const { search } = useLocation();
  const copy = COPY[languageId] || COPY.en;
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const isExpanded = expanded || pinned;
  const signedInUserName = typeof user?.name === "string" ? user.name.trim() : "";
  const logout = () => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.assign("/");
  };

  return (
    <div className={`corporateShell${isExpanded ? " corporateShell--expanded" : ""}${pinned ? " corporateShell--pinned" : ""}`} data-theme={themeId}>
      <aside
        className={`corporateSidebar${isExpanded ? " corporateSidebar--open" : ""}`}
        aria-label={copy.navigation}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocus={() => setExpanded(true)}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setExpanded(false); }}
      >
        <div className="corporateBrand">
          <Link className="corporateBrandMark" to="/select-workspace" aria-label={copy.workspace} title={copy.workspace}>
            <img src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`} alt="Pilcore" />
          </Link>
          <div className="corporateBrandText">
            <strong>Pilcore</strong>
            <span>{copy.title}</span>
            <small>{copy.scope}</small>
          </div>
          <button
            className="corporatePin" type="button" aria-pressed={pinned}
            aria-label={pinned ? copy.unpin : copy.pin} title={pinned ? copy.unpin : copy.pin}
            onClick={() => setPinned((value) => !value)}
          ><FiMapPin aria-hidden="true" /></button>
        </div>
        {signedInUserName ? (
          <div className="corporateSignedInUser" title={signedInUserName}>
            {signedInUserName.split(/(\s+)/).map((part, index) => (
              /\s/.test(part) ? part : <span className="corporateSignedInUserWord" key={index}>{part}</span>
            ))}
          </div>
        ) : null}
        <nav className="corporateNav" aria-label={copy.title}>
          {MENUS.map(({ key, Icon }) => (
            <NavLink
              key={key} end to={`/corporate-purchasing/${key}${search}`}
              title={copy.menus[key]} aria-label={copy.menus[key]}
              className={({ isActive }) => `corporateNavItem${isActive ? " corporateNavItem--active" : ""}`}
            >
              <Icon aria-hidden="true" />
              <span>{copy.menus[key]}</span>
            </NavLink>
          ))}
          <NavLink
            end to={`/corporate-purchasing/settings/users${search}`}
            title={copy.usersRoles} aria-label={copy.usersRoles}
            className={({ isActive }) => `corporateNavItem${isActive ? " corporateNavItem--active" : ""}`}
          >
            <FiUsers aria-hidden="true" /><span>{copy.usersRoles}</span>
          </NavLink>
        </nav>
        <div className="corporateSidebarFooter">
          <Link className="corporateNavItem" to="/select-workspace" title={copy.workspace} aria-label={copy.workspace}>
            <FiGrid aria-hidden="true" /><span>{copy.workspace}</span>
          </Link>
          <button className="corporateNavItem corporateLogout" type="button" onClick={logout} title={copy.logout} aria-label={copy.logout}>
            <FiLogOut aria-hidden="true" /><span>{copy.logout}</span>
          </button>
        </div>
      </aside>
      <main className="corporateMain"><Outlet /></main>
    </div>
  );
}
